import { CountryCode, CurrencyIsoCode, ShippingTier } from "src/enums";
import { normalizeItemName } from "@/libs/analytics/item-name";
import type {
  CartTrigger,
  CheckoutAuthMode,
  CheckoutEntryPoint,
} from "@/libs/analytics/cart-trigger";
import { CartItem, CartItemOption } from "@/store/cart-store";
import { Product, ProductPrice, ProductSlug } from "@/api/product-api";
import { OrderDraft, OrderItemV2 } from "@/models/orders/OrderDraft";
import {
  ProductCategory,
  AffiliationType,
  ItemBrandType,
} from "@/models/Product";
import { CartService } from "@/services/cart-service";
import { GameResell } from "@/models/GameResell";
import { GameItem } from "@/models/orders/OrderModelV2";
import { ProductTracker } from "@/utils/Tracker";
import { ProductUtil } from "@/utils/ProductUtil";
import { GA_ADS_ID, GA_ADS_SEND_TO, GA4_ID } from "./constants";

declare global {
  interface Window {
    gtag?: (
      command: "event" | "config" | "set" | "consent",
      targetId: string,
      config?: Record<string, any>,
    ) => void;
    fbq?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

export enum AnalyticsEvent {
  SearchTerm = "search_term",
  AppStarted = "app_started",

  ViewItem = "view_item",
  ViewItemList = "view_item_list",
  SelectItem = "select_item",
  AddToCart = "add_to_cart",
  RemoveFromCart = "remove_from_cart",
  ViewCart = "view_cart",
  BeginCheckout = "begin_checkout",
  AddShippingInfo = "add_shipping_info",
  Purchase = "purchase",

  Search = "search",

  CompatibilitySearchInput = "compatibility_search_input",
  CustomsDutyAndVat = "customs_duty_and_vat",

  WebViewSearchGame = "webview_search_game",
  WebViewBuyGame = "webview_buy_game",
  RecommendedTactSuits = "recommended_tactsuits",
  RecommendedTactSuitsPurchased = "recommended_tactsuits_purchased",

  // Where to Try (/business/where-to-try) — 국가 설정·최근접 정렬·필터·CTA
  WhereToTryLocationSet = "location_set",
  WhereToTryUseMyLocation = "use_my_location",
  WhereToTryFilterPartner = "filter_partner",
  WhereToTryVenueBook = "venue_book",
  WhereToTryVenueDirections = "venue_directions",

  // Coupon (AI10) — 쿠폰 적용 측정
  CouponApplied = "coupon_applied",

  // 출고 전 주문 취소 / EU 계약 철회 (#5093)
  OrderCancelClick = "order_cancel_click",
  OrderCancelSubmit = "order_cancel_submit",
  OrderCancelResult = "order_cancel_result",

  // 게임 상세 AI 햅틱 요약 카드(베타) — 노출 · 지원 기기 클릭(docs/haptic-summary-policy.md)
  HapticSummaryView = "haptic_summary_view",
  HapticSummaryDeviceClick = "haptic_summary_device_click",
  HapticSummaryLineClick = "haptic_summary_line_click",

  // 프로모션 표면(띠배너 · 홈 팝업 슬라이드 · TactSuit 히어로)의 노출·클릭 — GA4 표준 이벤트라
  // 「프로모션」 보고서에 그대로 쌓인다(use-promotion-tracking.ts).
  ViewPromotion = "view_promotion",
  SelectPromotion = "select_promotion",

  // 배송 후 반품 요청 / EU 계약 철회 (#5175)
  ReturnRequestButtonClick = "return_request_button_click",
  ReturnRequestFormView = "return_request_form_view",
  ReturnRequestSubmit = "return_request_submit",
  ReturnRequestSuccess = "return_request_success",
  ReturnRequestFail = "return_request_fail",
  ReturnRequestCancel = "return_request_cancel",
}

/**
 * 반품 요청 진입점. 푸터 링크는 주문 조회를 거쳐 같은 주문 상세로 도착하므로, 구분은
 * URL 파라미터(`?intent=withdrawal`)로만 남는다 — 상세 화면의 버튼 클릭과 섞이면 푸터 상시 링크의
 * 실사용량을 셀 수 없다(법정 진입점이라 유지 근거가 필요하다).
 */
export const ReturnRequestEntryPoint = {
  OrderDetail: "order-detail",
  Footer: "footer",
} as const;

export type ReturnRequestEntryPointValue =
  (typeof ReturnRequestEntryPoint)[keyof typeof ReturnRequestEntryPoint];

export interface CustomsDutyAndVatEventProps {
  tax_type: string;
  is_checked: boolean;
  country_code: string;
  shipping_type: string;
  purchase_amount: number;
  shipping_amount: number;
  currency: CurrencyIsoCode;
}

/**
 * ★**ITEM_PRICE_CONVENTION** — 퍼널 전 구간에서 item 금액은 한 규약을 쓴다 (#5190).
 *
 * ```
 * price    = originalAmount   (정가 / admin baseline)
 * discount = discountAmount   (할인액)
 * 사용자 결제 단가 = price - discount
 * ```
 *
 * `docs/order-item-pricing.md` 의 `OrderItem` 규약과 같은 축이고, `purchase`
 * (`orderItemsToAnalyticsItems`)·게임 경로 2종이 이미 이 규약이다.
 *
 * ⚠️ **`price` 에 순가를 넣지 말 것.** 종전 `add_to_cart`/`remove_from_cart` 가 순가를
 * `price` 로 넣고 `discount` 를 아예 생략해서, GA4 Items 리포트에서 같은 제품의 item 매출이
 * 카트 단계와 구매 단계에서 **다른 값**으로 잡혔다. 값이 비는 게 아니라 **틀린 값이 들어가**
 * 있어 리포트만 봐선 안 드러난다.
 *
 * ⚠️ **이벤트 레벨 `value` 는 순가 × 수량**이다(GA4 의 매출 지표). item 의 `price` 와 규약이
 * 다르다는 점이 헷갈리는 지점 — 둘을 같은 값으로 맞추려 하지 말 것.
 */
export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  item_sku: string;
  affiliation?: AffiliationType.Homepage;
  coupon?: string;
  currency?: CurrencyIsoCode;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

/**
 * 페이지뷰 추적
 */
export const pageview = (url: string) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("config", GA_ADS_ID, { page_path: url });
    window.gtag("config", GA4_ID, { page_path: url });
  }
};

/**
 * Google Ads 전환 추적
 */
export const trackConversion = (
  value: number,
  transaction_id: string,
  currency: string = "USD",
) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "conversion", {
      send_to: GA_ADS_SEND_TO,
      value,
      currency,
      transaction_id,
    });
  }
};

class Analytics {
  private static instance: Analytics;
  private initialized: boolean = false;

  private constructor() {}

  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  public init(): void {
    if (!this.initialized && typeof window !== "undefined" && window.gtag) {
      this.initialized = true;
    }
  }

  public viewItem = ({
    product,
    currency,
    countryCode,
  }: {
    product: Product;
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }) => {
    if (!product) return;

    const price = this.getPrice(product, currency, countryCode);
    if (!price) return;

    this.log(AnalyticsEvent.ViewItem, {
      currency: price.currency,
      value: price.originalAmount - price.discountAmount,
      items: [this.productToAnalyticsItem(product, currency, countryCode)],
    });
  };

  public viewItemList = ({
    products,
    listId,
    listName,
    currency,
    countryCode,
  }: {
    products: Product[];
    listId: string;
    listName: string;
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }) => {
    if (!products.length) return;

    this.log(AnalyticsEvent.ViewItemList, {
      item_list_id: listId,
      item_list_name: listName,
      items: products.map((product, index) => ({
        ...this.productToAnalyticsItem(product, currency, countryCode),
        index,
        item_list_id: listId,
        item_list_name: listName,
      })),
    });
  };

  public selectItem = ({
    product,
    listId,
    listName,
    index,
    currency,
    countryCode,
  }: {
    product: Product;
    listId: string;
    listName: string;
    index: number;
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }) => {
    if (!product) return;

    this.log(AnalyticsEvent.SelectItem, {
      item_list_id: listId,
      item_list_name: listName,
      items: [
        {
          ...this.productToAnalyticsItem(product, currency, countryCode),
          index,
          item_list_id: listId,
          item_list_name: listName,
        },
      ],
    });
  };

  public searchGames = ({
    term,
    resultsCount,
    pageTitle,
  }: {
    term: string;
    resultsCount?: number;
    pageTitle?: string;
  }) => {
    if (!term) return;

    this.search({
      searchTerm: term,
      resultsCount: resultsCount || 0,
      searchCategory: "game",
      pageTitle: pageTitle || "games",
    });
  };

  private search = ({
    searchTerm,
    resultsCount,
    searchCategory,
    pageTitle,
  }: {
    searchTerm: string;
    resultsCount: number;
    searchCategory: string;
    pageTitle: string;
  }) => {
    if (!searchTerm) return;

    this.log(AnalyticsEvent.SearchTerm, {
      search_term: searchTerm,
      page_title: pageTitle,
      custom_parameters: {
        search_category: searchCategory,
        results_count: resultsCount,
        user_type: "member",
      },
    });
  };

  public viewGameResell = (
    gameResell: GameResell | null | undefined,
    currency: CurrencyIsoCode,
  ) => {
    if (!gameResell) return;

    const item = this.gameResellToViewItems(gameResell, currency)[0];
    if (!item) return;

    // 통화는 요청값이 아니라 **아이템이 실제로 쓴 통화**다 — 폴백이 걸렸을 수 있다
    // (`gameResellToViewItems` 주석). 라벨과 숫자가 갈리면 #4975 의 기전이 된다.
    this.log(AnalyticsEvent.ViewItem, {
      currency: item.currency,
      value: (item.price ?? 0) - (item.discount ?? 0),
      items: [item],
    });
  };

  public logCustomsDutyAndVat = (props: {
    shippingAmount: number;
    countryCode: string;
    shippingType: string;
    purchaseAmount: number;
    currency: CurrencyIsoCode;
  }) => {
    this.log(AnalyticsEvent.CustomsDutyAndVat, {
      tax_type: "customs duty and vat",
      is_checked: true,
      country_code: props.countryCode,
      shipping_type: props.shippingType,
      purchase_amount: props.purchaseAmount,
      shipping_amount: props.shippingAmount,
      currency: props.currency,
    } as CustomsDutyAndVatEventProps);
  };

  /**
   * 출고 전 주문 취소(EU: 계약 철회) 퍼널 3종 (#5093 §관측/분석).
   * `is_eu`/`is_mixed` 는 EU 법정 플로우와 게임 키 혼합 주문을 분리 분석하기 위한 축이다.
   */
  public logOrderCancelClick = (props: {
    isEea: boolean;
    isMixed: boolean;
  }) => {
    this.log(AnalyticsEvent.OrderCancelClick, {
      is_eu: props.isEea,
      is_mixed: props.isMixed,
    });
  };

  public logOrderCancelSubmit = (props: {
    isEea: boolean;
    isMixed: boolean;
  }) => {
    this.log(AnalyticsEvent.OrderCancelSubmit, {
      is_eu: props.isEea,
      is_mixed: props.isMixed,
    });
  };

  /** 실패 시 `error_code` 로 사유(409 not-cancellable-state 등)를 남긴다 — silent fail 금지. */
  public logOrderCancelResult = (props: {
    success: boolean;
    isEea: boolean;
    isMixed: boolean;
    errorCode?: string;
  }) => {
    this.log(AnalyticsEvent.OrderCancelResult, {
      success: props.success,
      is_eu: props.isEea,
      is_mixed: props.isMixed,
      ...(props.errorCode ? { error_code: props.errorCode } : {}),
    });
  };

  /**
   * 반품 요청 버튼 클릭 (#5175). 진입점·주문 상태를 함께 남긴다 — 푸터 상시 링크(법정
   * 진입점)의 실사용량과 `shipped`/`delivered` 구간별 전환을 갈라 봐야 한다.
   */
  /** 게임 상세 AI 햅틱 요약 카드가 절반 이상 보였을 때 1회. `has_effects` = 이펙트 섹션도 있는 게임(칩 숨김) */
  public logHapticSummaryView = (props: {
    gameSlug: string;
    hasEffects: boolean;
    /** summary = AI 요약 · support = 원료가 없는 게임의 지원 타입 안내 — 두 카드의 지원 기기 클릭률을 가른다 */
    cardType: "summary" | "support";
  }) => {
    this.log(AnalyticsEvent.HapticSummaryView, {
      game_slug: props.gameSlug,
      has_effects: props.hasEffects,
      card_type: props.cardType,
    });
  };

  /** AI 햅틱 요약 카드의 「지원 기기 보기」 클릭 */
  /** 햅틱 요약 줄 클릭 — 그 줄의 근거(이펙트 「전체 보기」)를 열었다 */
  public logHapticSummaryLineClick = (props: {
    gameSlug: string;
    kind: string;
  }) => {
    this.log(AnalyticsEvent.HapticSummaryLineClick, {
      game_slug: props.gameSlug,
      kind: props.kind,
    });
  };

  /**
   * 프로모션 노출·클릭 (GA4 표준 `view_promotion` · `select_promotion`).
   *
   * `promotion_id`·`promotion_name` 은 **로케일과 무관한 고정 키**다 — 번역된 라벨을 넣으면 같은
   * 캠페인이 언어 수만큼 갈려 집계된다. `creative_slot` 은 어느 자리에서 보였나(띠·팝업·히어로).
   */
  public logViewPromotion = (props: { id: string; slot: string }) => {
    this.log(AnalyticsEvent.ViewPromotion, {
      promotion_id: props.id,
      promotion_name: props.id,
      creative_slot: props.slot,
    });
  };

  public logSelectPromotion = (props: { id: string; slot: string }) => {
    this.log(AnalyticsEvent.SelectPromotion, {
      promotion_id: props.id,
      promotion_name: props.id,
      creative_slot: props.slot,
    });
  };

  public logHapticSummaryDeviceClick = (props: { gameSlug: string }) => {
    this.log(AnalyticsEvent.HapticSummaryDeviceClick, {
      game_slug: props.gameSlug,
    });
  };

  public logReturnRequestButtonClick = (props: {
    entryPoint: ReturnRequestEntryPointValue;
    orderState: string;
    isEea: boolean;
  }) => {
    this.log(AnalyticsEvent.ReturnRequestButtonClick, {
      entry_point: props.entryPoint,
      order_state: props.orderState,
      is_eu: props.isEea,
    });
  };

  /** 폼(다이얼로그) 진입. 전용 페이지가 아니라 페이지뷰가 생기지 않으므로 명시 발화한다. */
  public logReturnRequestFormView = (props: { isEea: boolean }) => {
    this.log(AnalyticsEvent.ReturnRequestFormView, { is_eu: props.isEea });
  };

  /** 확정 버튼 클릭. 사유는 **선택 사항**이라 미선택(`has_reason=false`)이 정상값이다. */
  public logReturnRequestSubmit = (props: {
    isEea: boolean;
    reason?: string;
  }) => {
    this.log(AnalyticsEvent.ReturnRequestSubmit, {
      is_eu: props.isEea,
      has_reason: !!props.reason,
      ...(props.reason ? { reason: props.reason } : {}),
    });
  };

  public logReturnRequestSuccess = (props: { isEea: boolean }) => {
    this.log(AnalyticsEvent.ReturnRequestSuccess, { is_eu: props.isEea });
  };

  /** 실패 사유(409 종류)를 반드시 남긴다 — silent fail 금지. */
  public logReturnRequestFail = (props: {
    isEea: boolean;
    errorCode: string;
  }) => {
    this.log(AnalyticsEvent.ReturnRequestFail, {
      is_eu: props.isEea,
      error_code: props.errorCode,
    });
  };

  /** 반품 요청 되돌리기. 실패도 같은 이벤트에 `success=false` 로 남긴다. */
  public logReturnRequestCancel = (props: {
    success: boolean;
    errorCode?: string;
  }) => {
    this.log(AnalyticsEvent.ReturnRequestCancel, {
      success: props.success,
      ...(props.errorCode ? { error_code: props.errorCode } : {}),
    });
  };

  public addItem = ({
    slug,
    quantity = 1,
    name = "",
    sku,
    price,
    discount,
    currency,
    trigger = "unknown",
  }: {
    slug: ProductSlug;
    quantity?: number;
    name?: string;
    sku: string;
    /** ★정가(`originalAmount`). 순가가 아니다 — `ITEM_PRICE_CONVENTION` 참고. */
    price?: number;
    /** 할인액(`discountAmount`). */
    discount?: number;
    currency?: CurrencyIsoCode;
    /** 어느 진입점에서 담았는가 (#5292). 정의·기본값 사유는 `cart-trigger.ts`. */
    trigger?: CartTrigger;
  }) => {
    if (!slug) return;
    this.log(AnalyticsEvent.AddToCart, {
      trigger,
      ...(currency && { currency }),
      ...(price != null && { value: (price - (discount ?? 0)) * quantity }),
      items: [
        {
          item_id: slug,
          item_name: normalizeItemName(name, slug),
          item_sku: sku,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.bHaptics,
          item_category: ProductCategory.Hardware,
          ...(currency && { currency }),
          ...(price != null && { price }),
          ...(discount != null && { discount }),
          quantity: quantity,
        },
      ],
    });
  };

  public addGameItem = ({
    gameItem,
    currency,
    trigger = "unknown",
  }: {
    gameItem: GameItem | null;
    currency: CurrencyIsoCode;
    /**
     * 어느 진입점에서 담았는가 (#5292).
     *
     * ⚠️ **게임키도 `add_to_cart` 다.** 이슈가 `cart_sheet` 를 "장바구니 시트 내
     * 액세서리/**추천** 담기" 로 정의했고, 장바구니의 게임키 추천이 이 경로다.
     * 종전엔 이 이벤트에 `trigger` 필드가 **아예 없어** GA 에서 `unknown` 으로도
     * 안 드러났다 — 계측 공백이 공백으로도 안 보이는 상태였다.
     */
    trigger?: CartTrigger;
  }) => {
    if (!gameItem || !currency) return;
    this.log(AnalyticsEvent.AddToCart, {
      trigger,
      ...this.gameItemToCartParams({ gameItem, currency }),
    });
  };

  public deleteGameItem = ({
    gameItem,
    currency,
  }: {
    gameItem: GameItem | null;
    currency: CurrencyIsoCode;
  }) => {
    if (!gameItem || !currency) return;
    this.log(
      AnalyticsEvent.RemoveFromCart,
      this.gameItemToCartParams({ gameItem, currency }),
    );
  };

  private gameItemToCartParams = ({
    gameItem,
    currency,
  }: {
    gameItem: GameItem;
    currency: CurrencyIsoCode;
  }) => {
    const { name, gameResellId, platform } = gameItem;
    return {
      currency,
      value: gameItem.price - gameItem.discount,
      items: [
        {
          item_id: gameResellId,
          currency,
          price: gameItem.price,
          discount: gameItem.discount,
          item_name: `${name} (${platform})`,
          item_sku: `${gameResellId}:${platform}`,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.game,
          item_category: ProductCategory.Software,
          quantity: 1,
        },
      ],
    };
  };

  public removeItem = ({
    slug,
    sku,
    quantity,
    name,
    price,
    discount,
    currency,
  }: {
    slug: ProductSlug;
    sku: string;
    quantity: number;
    name?: string;
    /** ★정가(`originalAmount`). 순가가 아니다 — `ITEM_PRICE_CONVENTION` 참고. */
    price?: number;
    /** 할인액(`discountAmount`). */
    discount?: number;
    currency?: CurrencyIsoCode;
  }) => {
    if (!slug) return;

    this.log(AnalyticsEvent.RemoveFromCart, {
      ...(currency && { currency }),
      ...(price != null && { value: (price - (discount ?? 0)) * quantity }),
      items: [
        {
          item_id: slug,
          item_name: normalizeItemName(name, slug),
          item_sku: sku,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.bHaptics,
          item_category: ProductCategory.Hardware,
          ...(currency && { currency }),
          ...(price != null && { price }),
          ...(discount != null && { discount }),
          quantity: quantity,
        },
      ],
    });
  };

  public viewCart = ({
    cartItems,
    gameItems,
    totalPrice,
    currency,
    countryCode,
  }: {
    // ⚠️ 이름 없이 넘기면 `item_name` 이 슬러그 폴백으로 나가 같은 제품이 두 값으로
    // 갈린다. 폴백은 표시명과 **0/7** 로 다르다 — `TactSuit`·`TactGlove` 처럼 제품명이
    // 내부 대문자를 가져 `humanizeSlug` 로는 어떤 제품도 못 맞힌다.
    cartItems: (CartItem & Product)[];
    gameItems: GameItem[];
    totalPrice: number;
    currency: CurrencyIsoCode;
    countryCode: string | null;
  }) => {
    this.log(AnalyticsEvent.ViewCart, {
      currency,
      value: totalPrice,
      items: [
        ...this.cartItemsToAnalyticsItems(cartItems, currency, countryCode),
        ...this.gameItemsToAnalyticsItems(gameItems, currency),
      ],
    });
  };

  public clickCheckOutButtonEvent = ({
    cartItems,
    totalPayable,
    countryCode,
    gameItems,
    currency,
    entryPoint,
    authMode,
  }: {
    cartItems: (CartItem & Product)[];
    gameItems: GameItem[];
    totalPayable: number;
    countryCode: string;
    currency: CurrencyIsoCode;
    /** 어느 화면에서 시작했는가 (#5292). 정의는 `cart-trigger.ts`. */
    entryPoint: CheckoutEntryPoint;
    /** 발화 시점의 실제 인증 상태 (#5292). 버튼 이름이 아니다 — 그쪽 주석 참고. */
    authMode: CheckoutAuthMode;
  }) => {
    this.log(AnalyticsEvent.BeginCheckout, {
      entry_point: entryPoint,
      auth_mode: authMode,
      items: [
        ...this.cartItemsToAnalyticsItems(cartItems, currency, countryCode),
        ...this.gameItemsToAnalyticsItems(gameItems, currency),
      ],
      value: totalPayable,
      currency,
      country: countryCode,
    });
  };

  public addShippingInfo = (order: OrderDraft) => {
    const { items, currency, shippingCountry, subtotalAmount, referralCode } =
      order;
    const shipping_tier = this.getShippingTier(shippingCountry);

    this.log(AnalyticsEvent.AddShippingInfo, {
      currency,
      value: subtotalAmount,
      coupon: referralCode,
      shipping_tier,
      items: this.orderItemsToAnalyticsItems(items, currency),
    });
  };

  public purchased = (order: {
    orderId: string;
    items: OrderItemV2[];
    gameItems: GameItem[];
    currency: CurrencyIsoCode;
    shippingCountry: string;
    subtotalAmount: number;
    shippingPrice: number;
    referralCode: string;
    paymentMethod: string;
    couponCode?: string;
  }) => {
    const {
      orderId,
      items,
      gameItems,
      currency,
      shippingCountry,
      subtotalAmount,
      shippingPrice,
      referralCode,
      paymentMethod,
      couponCode,
    } = order;
    const shipping_tier = this.getShippingTier(shippingCountry);

    this.log(AnalyticsEvent.Purchase, {
      // transaction_id=orderId(주문번호)는 mgmt 서버사이드 purchase(MP)와의 GA4 dedup 계약 —
      // 같은 값이어야 클라/서버 발화가 한 거래로 합쳐진다(mgmt docs/ga4-measurement-protocol.md).
      // 32자 hex order.id 를 쓰면 MP(orderId)와 어긋나 이중 계수된다(#5037 거래 과대).
      transaction_id: orderId,
      items: [
        ...this.orderItemsToAnalyticsItems(items, currency),
        ...this.gameItemsToAnalyticsItems(gameItems, currency),
      ],
      currency,
      value: subtotalAmount,
      shipping: shippingPrice,
      shipping_tier,
      coupon: couponCode || referralCode,
      payment_type: paymentMethod?.toLowerCase(),
    });

    // Google Ads 전환(trackConversion)은 "advertising" 동의 소관이라 여기서 호출하지 않음.
    // 호출부(trackPurchase)가 ConsentCategory.Advertising 블록 안에서 직접 호출.
    this.reportRecommendedTactSuitsPurchased(order.items);
  };

  public couponApplied = (p: { code: string; draftId: string }) => {
    this.log(AnalyticsEvent.CouponApplied, {
      coupon: p.code,
      draft_id: p.draftId,
    });
  };

  public compatibilitySearchInput = (search_term: string) => {
    if (search_term.length <= 0) return;

    this.log(AnalyticsEvent.CompatibilitySearchInput, { search_term });
  };

  public reportRecommendedTactSuits = ({
    keywords,
    productSlugs,
  }: {
    keywords: string[];
    productSlugs: string[];
  }) => {
    this.log(AnalyticsEvent.RecommendedTactSuits, {
      chosen_keywords: keywords.join(","),
      product_slugs: productSlugs.join(","),
      total_keywords: keywords.length,
    });
    ProductTracker.track(productSlugs);
  };

  public reportRecommendedTactSuitsPurchased = (orderItems: OrderItemV2[]) => {
    const recommendations = ProductTracker.getAll();
    const clues = orderItems
      .filter((i) => recommendations.some((r) => r.productSlug === i.slug))
      .map((i) => {
        const recommendation = recommendations.find(
          (r) => r.productSlug === i.slug,
        );
        const timeToConversion =
          Date.now() - (recommendation?.createdAt || Date.now());
        return {
          ...i,
          time_to_conversion_seconds: Math.floor(timeToConversion / 1000),
        };
      });
    if (clues.length === 0) return;
    this.log(AnalyticsEvent.RecommendedTactSuitsPurchased, {
      purchased_products: clues.map((c) => ({
        product_slug: c.slug,
        product_name: c.name,
        product_sku: c.sku,
        product_qty: c.qty,
        time_to_conversion_seconds: c.time_to_conversion_seconds,
      })),
      recommended_products: recommendations.map((r) => ({
        product_slug: r.productSlug,
        recommended_at: new Date(r.createdAt).toISOString(),
      })),
    });
    ProductTracker.untrack(clues.map((c) => c.slug));
  };

  public logWebViewEvent = (
    event: AnalyticsEvent,
    params: Record<string, any> = {},
  ) => {
    this.log(event, {
      ...params,
      environment: "webview",
    });
  };

  private log = (event: any = AnalyticsEvent.AppStarted, params: any = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", event, params);
    }
  };

  private productToAnalyticsItem = (
    product: Product,
    currency: CurrencyIsoCode,
    countryCode: string | null,
    quantity: number = 1,
  ): AnalyticsItem => {
    const { name, slug } = product;
    const price = this.getPrice(product, currency, countryCode);

    return {
      item_id: slug,
      // 표시명이지만 정규화는 거친다 — 인코딩 깨짐·공백이 그대로 나가면 같은 제품이
      // 별도 품목으로 집계된다(#5292 C: "모든 발화 지점에서 표시명 기준으로 통일").
      item_name: normalizeItemName(name, slug),
      item_sku: slug,
      affiliation: AffiliationType.Homepage,
      item_brand: ItemBrandType.bHaptics,
      item_category: ProductCategory.Hardware,
      // ★item-level `currency` 를 반드시 싣는다 (#5190). `view_item_list`·`select_item` 은
      // 이벤트에 `value` 가 없어 **이벤트 레벨 `currency` 를 붙일 자리가 없다** — 그래서
      // 종전엔 USD 금액이 **통화 라벨 없이** 나갔다. GA4 item 파라미터 `currency` 는 있으면
      // 이벤트 레벨을 덮으므로, 금액을 만든 쪽이 통화도 같이 싣는 게 유일하게 안전한 형태다.
      // (FbPixel.ts:300 의 같은 취지 주석 참고 — EUR 금액에 USD 라벨이 붙었던 전례)
      currency: price?.currency,
      discount: price?.discountAmount,
      price: price?.originalAmount,
      quantity: quantity,
    };
  };

  /**
   * 게임키 `view_item` 아이템. 표시 통화로 집되, **없으면 USD 로 폴백**한다 (#5190).
   *
   * ⚠️ 폴백이 필요한 이유 — 게임 가격은 `ShopProduct` 가 아니라 `GameResell.platformList[].
   * priceList[]` 에 있고 **별도 DB(`Game Key`)** 로 관리된다. EU/GB 통화가 카탈로그에 늦게
   * 들어온 전례가 있어(admin#1430 → #1442) 요청 통화가 목록에 없을 수 있다. 폴백 없이
   * 정확 일치만 하면 그 게임은 **아이템이 통째로 드롭**돼 `view_item` 이 무음이 된다.
   *
   * ★**폴백한 통화를 라벨에도 반영한다.** 통화 선언과 실제 숫자가 어긋나면 EUR 금액에 USD
   * 라벨이 붙는 식이 되고, 그게 카트/체크아웃에서 과다청구를 만든 #4975 의 기전이다.
   * 그래서 `price.currency`(=실제로 고른 가격의 통화)를 싣지, 요청 통화를 싣지 않는다.
   */
  private gameResellToViewItems = (
    gameResell: GameResell,
    currency: CurrencyIsoCode,
  ): AnalyticsItem[] => {
    const { id, name, platformList } = gameResell;

    return platformList
      .map((platform) => {
        const price =
          platform.priceList.find((p) => p.currency === currency) ??
          platform.priceList.find((p) => p.currency === CurrencyIsoCode.USD);
        if (!price) return null;

        return {
          item_id: id,
          item_name: `${name} (${platform.platform})`,
          item_sku: `${id}:${platform}`,
          affiliation: AffiliationType.Homepage,
          item_brand: ItemBrandType.game,
          item_category: ProductCategory.Software,
          currency: price.currency,
          discount: price.discount,
          price: price.price,
          quantity: 1,
        };
      })
      .filter((item) => item !== null);
  };

  /**
   * `view_cart`·`begin_checkout` 의 items.
   *
   * ⚠️ 입력이 `ProductInfo`(= `Product & CartItem`)인 것은 **우연이 아니다** — 호출처 3곳
   * (`cart-page`·`cart-checkout-button`·`useCheckout`)이 전부 `useCartWithProducts().items`
   * 계열을 넘긴다. 종전 시그니처는 `CartItem & { name?: string }` 이라 이름만 겨우 받았고,
   * 그래서 **item 금액을 아예 못 실었다**(#5190). 여기서 Product 를 받으면 `add_to_cart`·
   * `purchase` 와 **같은 `getProductPrice` 호출**로 금액을 만들 수 있다.
   */
  private cartItemsToAnalyticsItems = (
    cartItems: (CartItem & Product)[],
    currency: CurrencyIsoCode,
    countryCode: string | null,
  ): AnalyticsItem[] => {
    return cartItems.map((cartItem) => {
      const price = this.getPrice(cartItem, currency, countryCode);

      return {
        item_id: cartItem.slug,
        // ★가장 큰 오염원이었다 — 여기가 **항상 슬러그**였고 `begin_checkout` 이 이 경로다.
        item_name: normalizeItemName(cartItem.name, cartItem.slug),
        item_sku: cartItem.sku,
        // 아이템이 실제로 고른 가격의 통화. 못 고르면(가격 결측) 요청 통화로 라벨만 남긴다 —
        // 금액이 없으므로 라벨과 숫자가 갈릴 여지가 없다.
        currency: price?.currency ?? currency,
        affiliation: AffiliationType.Homepage,
        item_brand: ItemBrandType.bHaptics,
        item_category: ProductCategory.Hardware,
        item_variant: JSON.stringify(cartItem.options),
        // ITEM_PRICE_CONVENTION — 정가 + 할인액. 순가를 `price` 에 넣지 않는다.
        ...(price && { price: price.originalAmount }),
        ...(price && { discount: price.discountAmount }),
        quantity: cartItem.quantity,
      };
    });
  };

  private gameItemsToAnalyticsItems = (
    gameItems: GameItem[],
    currency: CurrencyIsoCode,
  ): AnalyticsItem[] => {
    return gameItems.map((gameItem) => ({
      item_id: gameItem.gameResellId,
      item_name: `${gameItem.name} (${gameItem.platform})`,
      item_sku: `${gameItem.gameResellId}:${gameItem.platform}`,
      currency,
      discount: gameItem.discount,
      price: gameItem.price,
      affiliation: AffiliationType.Homepage,
      item_brand: ItemBrandType.game,
      item_category: ProductCategory.Software,
      quantity: 1,
    }));
  };

  private orderItemsToAnalyticsItems = (
    items: OrderItemV2[],
    currency: CurrencyIsoCode,
  ): AnalyticsItem[] => {
    return items.map((item) => ({
      item_id: item.slug,
      item_name: normalizeItemName(item.name, item.slug),
      item_sku: item.sku,
      currency: currency,
      affiliation: AffiliationType.Homepage,
      item_brand: ItemBrandType.bHaptics,
      item_category: ProductCategory.Hardware,
      item_variant: JSON.stringify(item.options),
      price: item.price,
      discount: item.discount,
      quantity: item.qty,
    }));
  };

  private getShippingTier = (countryCode: string) => {
    if (countryCode === CountryCode.KR) {
      return ShippingTier.Ground;
    }

    return ShippingTier.Air;
  };

  /**
   * 퍼널 이벤트에 실을 제품 가격. **표시·청구 통화 그대로** 집는다 (#5190).
   *
   * 종전엔 `getUSDPrice` 라는 이름으로 `currency: USD` 를 하드코딩했다. 그래서 KR 사용자가
   * ₩749,000 제품을 보면 `view_item` 만 `USD/529` 로 나가고 `add_to_cart` 부터 `KRW/749000` 이
   * 돼 한 퍼널 안에 기준 통화가 둘이었다 — GA4 는 이벤트별로 각각 환산하므로 집계는 깨지지
   * 않지만 "조회 금액 → 구매 금액" 분석이 무의미해진다.
   *
   * ★**카트·주문과 같은 식이어야 한다.** 여기 인자 3개(`product`·`currency`·`countryCode`)는
   * `cart-store` 의 `getItemPrice`(add_to_cart)·`OrderService.generateOrderItem`(purchase)과
   * **같은 `ProductUtil.getProductPrice` 호출**이다. 통화 소스도 셋 다 `useCurrency()` 로
   * 정렬돼 있다. 하나라도 다른 축을 쓰면 퍼널이 다시 갈라진다.
   */
  private getPrice = (
    product: Product,
    currency: CurrencyIsoCode,
    countryCode: string | null,
  ): ProductPrice | null => {
    return ProductUtil.getProductPrice({
      currency,
      countryCode,
      product,
    });
  };

  // ── Where to Try 이벤트 (Consent Mode 게이팅은 log() 공통 처리) ──────────────
  public whereToTryLocationSet = (p: {
    country: string;
    auto: boolean;
    source: string;
  }) => this.log(AnalyticsEvent.WhereToTryLocationSet, p);
  public whereToTryUseMyLocation = (p: { country: string | null }) =>
    this.log(AnalyticsEvent.WhereToTryUseMyLocation, p);
  public whereToTryFilterPartner = (p: { partner: string | null }) =>
    this.log(AnalyticsEvent.WhereToTryFilterPartner, p);
  public whereToTryVenueBook = (p: { venue: string; country?: string }) =>
    this.log(AnalyticsEvent.WhereToTryVenueBook, p);
  public whereToTryVenueDirections = (p: { venue: string; country?: string }) =>
    this.log(AnalyticsEvent.WhereToTryVenueDirections, p);
}

const analyticsInstance: Analytics | undefined =
  typeof window !== "undefined" ? Analytics.getInstance() : undefined;

export default analyticsInstance;
